import { capitalize } from '@/js/FormFunctions';

/**
 * Including this mixin in to an input type component so that validation occurs on input/change events.
 *
 * @type {{
 * props: {fieldName: StringConstructor, label: StringConstructor, message: StringConstructor,
 * validation: {type: ObjectConstructor, default: (function(): {$error: boolean, $touch: $touch})}},
 * methods: {isError: (function(*=, *=): boolean)},
 * computed: {hasRequired: ValidationMixin.computed.hasRequired, errorMessage: ValidationMixin.computed.errorMessage}}}
 */
const ValidationMixin = {
  props: {
    fieldName: String,
    label: String,
    message: String,
    validation: {
      type: Object,
      default() {
        return {
          $error: false,
          $touch() {},
        };
      },
    },
  },
  methods: {
    isError(obj, property) {
      return Object.prototype.hasOwnProperty.call(obj, property) && !obj[property];
    },
  },
  computed: {
    hasRequired(context = null) {
      if (this.validation.$params
        && this.validation.$params.required
        && this.validation.$params.required.type === 'requiredIf'
        && typeof this.validation.$params.required.prop === 'function') {
        if (context && context.$parent) {
          return this.validation.$params.required.prop.call(context.$parent, this);
        }
      }
      return Object.prototype.hasOwnProperty.call(this.validation, 'required');
    },
    errorMessage() {
      if (this.message) {
        return this.message;
      }
      const errorLabel = this.label || this.fieldName || 'This field';

      if (errorLabel) {
        const val = this.validation;
        if (this.isError(val, 'required')) {
          return `${errorLabel} is required.`;
        }
        if (this.isError(val, 'minLength')) {
          return `${errorLabel} does not meet the minimum length.`;
        }
        if (this.isError(val, 'email')) {
          return `A valid email is required for ${errorLabel}.`;
        }
        if (this.isError(val, 'sameAs')) {
          return `${errorLabel} must match.`;
        }
        if (this.isError(val, 'numeric')) {
          return `${errorLabel} must be a number.`;
        }
        if (this.isError(val, 'minDateValue')) {
          return `${errorLabel} must be greater than the current value.`;
        }
        if (this.isError(val, 'maxDateValue')) {
          return `${errorLabel} must be less than the current value.`;
        }
        if (this.isError(val, 'minValue')) {
          return `${errorLabel} must be greater than the current value.`;
        }
        if (this.isError(val, 'maxValue')) {
          return `${errorLabel} must be less than the current value.`;
        }
        if (this.isError(val, 'dateDifference')) {
          return 'Invalid date range.';
        }
        if (this.isError(val, 'maxFileSize')) {
          return `File exceeds maximum size of ${(val.$params.maxFileSize.maxFileSize / 1024) / 1024}MB.`;
        }
        if (this.isError(val, 'lessThan')) {
          return 'Input must be less than the current value';
        }
      }
      return '';
    },
  },
};

const CapitalizeFilter = {
  filters: {
    capitalize,
  },
};

export { ValidationMixin, CapitalizeFilter };
